import { browserHistory } from 'react-router';
import {
  autorunAsync,
  computed,
  extendObservable
} from 'mobx';
import { getNextSoundDate } from '../utils/alarms';
import {
  addSecondsChangeEvent,
  removeSecondsChangeEvent,
  getTimeUntil
} from '../utils/datetime';
import { leftPad } from '../utils/string';
import { createAlarm } from '../requests/createAlarm';
import { deleteAlarm } from '../requests/deleteAlarm';
import { getAlarms } from '../requests/getAlarms';
import { updateAlarm } from '../requests/updateAlarm';

const SNOOZE_MINUTES = 5;

const getMinuteDateID = function (hours = new Date().getHours(), minutes = new Date().getMinutes()) {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0).valueOf();
}

class Alarm {
	constructor(store, id, data) {
    extendObservable(this, {
      days: data.days,
      hours: data.hours,
      is_active: data.is_active,
      lastSound: undefined,
      todaySound: computed(() => (
        getMinuteDateID(this.hours, this.minutes)
      )),
      nextSoundDate: computed(() => {
        return getNextSoundDate({
          days: this.days,
          hours: this.hours,
          minutes: this.minutes
        });
      }),
      minutes: data.minutes,
      timeString24Hours: computed(() => (
        `${leftPad(this.hours)}:${leftPad(this.minutes)}`
      )),
      timeString12Hours: computed(() => {
        let hours = this.hours === 0 ? 12 : this.hours > 12 ? this.hours - 12 : this.hours;
        return `${leftPad(hours)}:${leftPad(this.minutes)}`
      }),
      timePeriod: computed(() => this.hours >= 12 ? 'pm' : 'am'),
      timeUntil: undefined,

      asJSON: computed(() => ({
        days: this.days,
        hours: this.hours,
        id: this.id,
        is_active: this.is_active,
        minutes: this.minutes,
      }))
    });
		this.store = store;
		this.id = id;

    if (this.id) {
      this._saveHandle = autorunAsync(() => {
        updateAlarm(this.id, this.asJSON, function(resp) {
          console.log(resp);
        });
      }, 2000);
    }

    this._init();
	}

  update(data) {
    if (data.days) this.days = data.days;
    if (data.hours) this.hours = data.hours;
    if (data.minutes) this.minutes = data.minutes;
    if (typeof data.is_active === 'boolean') this.is_active = data.is_active;
  }

  toggleActiveState() {
    if (this.is_active) {
      this.deactivate();
    } else {
      this.activate();
    }
  }

  _init() {
    if (this.is_active) {
      this.activate();
    }
  }

  activate() {
    this.is_active = true;

    addSecondsChangeEvent(`alarm_${this.id}`, date => {
      // time is now and didn't already sound
      // time is passed, but snooze set and matches time + snooze
      if (this.shouldSound(date)) {
        this.sound();
      }

      this.timeUntil = getTimeUntil(this.nextSoundDate);
    });
  }

  deactivate() {
    this.is_active = false;
    removeSecondsChangeEvent(`alarm_${this.id}`);
  }

  delete() {
    deleteAlarm(this.id, resp => {
      if (resp.success) {
        this.store.alarms.remove(this);
      }
    });
    removeSecondsChangeEvent(`alarm_${this.id}`);
  }

  shouldSound(date) {
    return (
      this.is_active &&
      this.days.includes(date.getDay() + 1) &&
      this.hours === date.getHours() &&
      this.minutes === date.getMinutes() &&
      this.lastSound !== getMinuteDateID()
    ) || (
      this.is_active &&
      this.snoozeDateID === getMinuteDateID() &&
      this.lastSound !== getMinuteDateID()
    );
  }

  sound() {
    this.lastSound = getMinuteDateID();
    browserHistory.push(`/alarm/${this.id}`);
  }

  snooze() {
    const now = new Date();
    this.snoozeDateID = getMinuteDateID(now.getHours(), now.getMinutes() + SNOOZE_MINUTES);
  }
}


export default class AlarmStore {
  constructor() {
    extendObservable(this, {
      alarms: [],
      activeCount: computed(() => this.alarms.filter(alarm => alarm.is_active).length),
      upcoming: computed(() => this.alarms
        .filter(alarm => alarm.is_active)
        .sort(this._nextSoundSorter)
      ),
      next: computed(() => {
        return this.upcoming[0];
      }),
      isLoading: true
    });

    this._loadData();
    this._loadEvents = [];
  }

  _loadData() {
    getAlarms(alarms => {
      this.alarms = alarms
        .map(alarm => new Alarm(this, alarm.id, alarm))
        .sort(this._listSorter);

      if (this._loadEvents.length) {
        this._loadEvents.forEach(event => event(this));
        this._loadEvents = [];
      }
      this.isLoading = false;
    });
  }

  _listSorter(a, b) {
    return ((a.hours * 100) + a.minutes) - ((b.hours * 100) + b.minutes);
  }

  _nextSoundSorter(a, b) {
    return a.nextSoundDate.valueOf() - b.nextSoundDate.valueOf();
  }

  add(alarmData) {
    createAlarm(alarmData, resp => {
      if (resp.success) {
        this.alarms = this.alarms
          .concat(new Alarm(this, resp.result.id, resp.result))
          .sort(this._listSorter);
      }
    });
  }

  get(alarmID, fn) {
    if (this.isLoading) {
      this._loadEvents.push(store => {
        return fn(store.alarms.find(alarm => alarm.id === alarmID));
      });
    } else {
      return fn(this.alarms.find(alarm => alarm.id === alarmID));
    }
  }

  onLoad(fn) {
    if (this.isLoading) {
      this._loadEvents.push(store => {
        return fn(store);
      });
    } else {
      return fn(this);
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/AlarmStore.js